import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { toLibraryError, unwrap } from '../../library/core/library-api';
import { ApiEnvelope } from '../../library/core/library.models';
import { WinPage, WinSubmitBody, WinSubmitResult, WinUpload, WinUploadSlot } from './win.models';

/** `https://<job-ops-api>/api/story/win` — the public vendor page endpoints. */
export const WIN_API_BASE = `${(environment.apiBaseUrl ?? '').replace(/\/+$/, '')}/api/story/win`;

/** The header that carries the emailed token. It is never put in a URL, so it cannot end up in any log. */
export const WIN_TOKEN_HEADER = 'X-Story-Token';

export function isWinUrl(url: string): boolean {
  return url === WIN_API_BASE || url.startsWith(`${WIN_API_BASE}/`);
}

/**
 * The vendor "Show us the win" page API. No login: the token from the link is the only credential. Errors keep the
 * server's own message and carry the correlation id (the Application Insights operation_Id) for support.
 */
@Injectable({ providedIn: 'root' })
export class WinApiService {
  private readonly http = inject(HttpClient);

  private options(token: string): { headers: HttpHeaders } {
    return { headers: new HttpHeaders({ [WIN_TOKEN_HEADER]: token }) };
  }

  load(token: string): Observable<WinPage> {
    return this.http.get<ApiEnvelope<WinPage>>(WIN_API_BASE, this.options(token)).pipe(this.read());
  }

  createUpload(token: string, body: { fileName: string; contentType: string; bytes: number }): Observable<WinUploadSlot> {
    return this.http
      .post<ApiEnvelope<WinUploadSlot>>(`${WIN_API_BASE}/uploads`, body, this.options(token))
      .pipe(this.read());
  }

  completeUpload(token: string, uploadId: string): Observable<WinUpload> {
    return this.http
      .post<ApiEnvelope<WinUpload>>(`${WIN_API_BASE}/uploads/${uploadId}/complete`, {}, this.options(token))
      .pipe(this.read());
  }

  removeUpload(token: string, uploadId: string): Observable<void> {
    return this.http.delete<ApiEnvelope<object>>(`${WIN_API_BASE}/uploads/${uploadId}`, this.options(token)).pipe(
      map(() => undefined),
      catchError((err: unknown) => throwError(() => toLibraryError(err))),
    );
  }

  submit(token: string, body: WinSubmitBody): Observable<WinSubmitResult> {
    return this.http
      .post<ApiEnvelope<WinSubmitResult>>(`${WIN_API_BASE}/submit`, body, this.options(token))
      .pipe(this.read());
  }

  private read<T>() {
    return (source: Observable<ApiEnvelope<T>>): Observable<T> =>
      source.pipe(
        map((res) => unwrap(res)),
        catchError((err: unknown) => throwError(() => toLibraryError(err))),
      );
  }
}
